import { MapPinIcon } from "@heroicons/react/24/solid";
import { zodResolver } from "@hookform/resolvers/zod";
import { LocationFormProps } from "data/reduxInterfaces";
import { FC, Fragment, HTMLAttributes, useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import Input from "shared/Input/Input";
import { z } from "zod";
import FormItem from "./FormItem";
import usePlacesService from "react-google-autocomplete/lib/usePlacesAutocompleteService";
import { List } from "@mui/material";
import { Card, ListItem } from "@material-tailwind/react";
import toast from "react-hot-toast";

const formSchema = z.object({
  city: z.string().min(1, { message: "Le ville est obligatoire." }),
  address: z.string().min(1, { message: 'L"addresse est obligatoire.' }),
  address2: z.string().optional(),
  country: z.string().optional(),
  codePostale: z
    .number({
      required_error: "Le code postale est obligatoire.",
      invalid_type_error: "Le code postale doit étre un nombre.",
    })
    .min(4, { message: "Le code postal doit comporter au moins 4 chiffres." }),
  map: z.string().optional(),
});

const LocationAddress: FC<LocationFormProps & HTMLAttributes<HTMLDivElement>> = ({
  isLoading,
  handleForm,
  data,
  FormData,
}) => {
  const [isload, setIload] = useState(isLoading);
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      city: data?.city ?? "",
      address: data?.address ?? "",
      address2: data?.address2 ?? "",
      country: data?.country ?? "",
      codePostale: data?.codePostale ?? undefined,
      map: data?.map ?? "",
    },
    mode: "onSubmit",
  });

  const { placesService, placePredictions, isPlacePredictionsLoading, getPlacePredictions } = usePlacesService({
    apiKey: process.env.REACT_APP_MAPS_API_KEY + "&loading=async",
    options: {
      input: "",
      componentRestrictions: {
        country: "fr",
      },
    },
  });

  useEffect(() => {
    if (data) {
      setValue("city", data.city);
      setValue("address", data.address);
      setValue("address2", data.address2);
      setValue("codePostale", data.codePostale);
    }
  }, [data]);

  useEffect(() => {
    if (handleForm.type === "chekLocationFormTwo") {
      handleSubmit(onSubmit)();
    }
  }, [handleForm]);

  const holdeSelectAddress = async (place_id: string) => {
    setIload(true);
    placesService?.getDetails({ placeId: place_id }, (placeDetails: any, placeStatus: any) => {
      if (placeStatus !== "OK") {
        console.error("🔴 Google Places API Error in LocationAddress:", placeStatus);
        console.error("🔴 Place ID:", place_id);
        console.error("🔴 Place Details:", placeDetails);
        // Ne pas afficher l'erreur si c'est juste un problème de loading initial ou si place_id est vide
        if (place_id && place_id.trim() !== "" && placeStatus !== "ZERO_RESULTS") {
          toast.error("Erreur lors de la récupération de l'adresse, veuillez réessayer!");
        }
        return; // Sortir si erreur
      }
      const parser = new DOMParser();
      const adr_address = parser.parseFromString(placeDetails?.adr_address ?? "", "text/html");
      const streetAddress = adr_address.querySelector(".street-address")?.textContent || "";
      const postalCode = adr_address.querySelector(".postal-code")?.textContent || "";
      const locality = adr_address.querySelector(".locality")?.textContent || "";
      const country_name = adr_address.querySelector(".country-name")?.textContent || "";

      if (streetAddress !== placeDetails?.name) {
        setValue("address", `${placeDetails?.name}, ${streetAddress}`);
      } else {
        setValue("address", streetAddress);
      }
      setValue("codePostale", Number(postalCode));
      setValue("city", locality);
      setValue("country", country_name);
      setValue("map", placeDetails?.url ?? "");

      setIload(false);
    });

    getPlacePredictions({ input: "" });
  };

  const onSubmit: SubmitHandler<z.infer<typeof formSchema>> = (data) => {
    FormData(data);
  };

  return (
    <Fragment>
      <form className="w-full" onSubmit={handleSubmit(onSubmit)}>
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-2">Localisation & Accès</h2>
          <div className="border-b w-14 border-neutral-200 dark:border-neutral-700"></div>
        </div>

        {/* Formulaire d'adresse */}
        <div className="space-y-6">
          <FormItem label="Adresse principale *">
            <div className="relative">
              <Input
                className={`${errors.address?.message ? "border-red-500" : ""}`}
                disabled={isLoading || isload}
                placeholder="Ex: 123 Avenue de la République"
                {...register("address")}
                onChange={(e) => {
                  setValue("address", e.target.value);
                  getPlacePredictions({ input: e.target.value });
                }}
                onKeyUp={(e) => {
                  if (e.key === "Escape") {
                    getPlacePredictions({ input: "" });
                  }
                }}
                loading={isPlacePredictionsLoading || isload}
              />

              {placePredictions.length > 0 && (
                <div className="absolute top-full left-0 right-0 z-50 mt-1">
                  <Card className="w-full shadow-lg">
                    <List className="max-h-60 overflow-y-auto">
                      {placePredictions.map((item, index) => (
                        <ListItem
                          key={index}
                          onClick={() => holdeSelectAddress(item.place_id)}
                          className="hover:bg-gray-100 cursor-pointer text-sm"
                        >
                          {item.description}
                        </ListItem>
                      ))}
                    </List>
                  </Card>
                </div>
              )}

              {errors.address?.message && (
                <div className="text-xs text-red-500 mt-1">{errors.address.message}</div>
              )}
            </div>
          </FormItem>

          <FormItem label="Complément d'adresse (optionnel)">
            <Input
              disabled={isLoading || isload}
              placeholder="Ex: Appartement 4B, 2ème étage"
              {...register("address2")}
            />
          </FormItem>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormItem label="Ville *">
              <Input
                className={`${errors.city?.message ? "border-red-500" : ""}`}
                disabled={isLoading || isload}
                placeholder="Ex: Paris"
                {...register("city")}
              />
              {errors.city?.message && <div className="text-xs text-red-500 mt-1">{errors.city.message}</div>}
            </FormItem>

            <FormItem label="Code postal *">
              <Input
                className={`${errors.codePostale?.message ? "border-red-500" : ""}`}
                disabled={isLoading || isload}
                placeholder="Ex: 75001"
                type="number"
                {...register("codePostale", { valueAsNumber: true })}
              />
              {errors.codePostale?.message && (
                <div className="text-xs text-red-500 mt-1">{errors.codePostale.message}</div>
              )}
            </FormItem>
          </div>
        </div>
      </form>
    </Fragment>
  );
};

export default LocationAddress;
